import React, { useState, useEffect, useRef } from "react";
import { MicOutline, Stop, RefreshOutline, CheckmarkCircle, VolumeHighOutline } from "react-ionicons";

const PronunciationPractice = (props) => {
    const { data, markedAsDone } = props;
    const moduloactual = data.moduloactual;

    const [activeListeningIndex, setActiveListeningIndex] = useState(null);
    const [cardTranscripts, setCardTranscripts] = useState({});
    const [cardScores, setCardScores] = useState({});
    const [cardPassed, setCardPassed] = useState({});
    const [recognitionError, setRecognitionError] = useState("");
    const [phonetics, setPhonetics] = useState({});
    const [playingIndex, setPlayingIndex] = useState(null);

    const recognitionRef = useRef(null);

    const cleanWord = (word) => word.toLowerCase().replace(/[^a-z0-9]/g, "");

    // Extract target lines from item description
    const { structuredLines, allCleanWords } = React.useMemo(() => {
        const html = moduloactual.cursos_item.descripcion || "";
        let formatted = html
            .replace(/<p[^>]*>/gi, "")
            .replace(/<\/p>/gi, "\n")
            .replace(/<div[^>]*>/gi, "")
            .replace(/<\/div>/gi, "\n")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/&nbsp;/g, " ");
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = formatted;
        const text = tempDiv.textContent || tempDiv.innerText || "";

        const allClean = [];
        const lines = [];

        text.split("\n").forEach((line) => {
            const trimmed = line.trim();
            if (trimmed.length > 0) {
                const words = trimmed.split(/\s+/).filter((w) => w.length > 0);
                const lineWords = words.map((word) => {
                    const clean = cleanWord(word);
                    allClean.push(clean);
                    return { word, clean };
                });
                lines.push({
                    index: lines.length,
                    rawLine: trimmed,
                    words: lineWords
                });
            }
        });

        return { structuredLines: lines, allCleanWords: allClean };
    }, [moduloactual.cursos_item.descripcion]);

    // Pre-load phonetics for words
    useEffect(() => {
        const fetchPhonetics = async () => {
            for (const word of allCleanWords) {
                if (!word || phonetics[word]) continue;
                try {
                    let phoneticText = "";
                    try {
                        const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);
                        if (res.ok) {
                            const json = await res.json();
                            if (json && json.length > 0) {
                                phoneticText = json[0].phonetic;
                                if (!phoneticText && json[0].phonetics) {
                                    const p = json[0].phonetics.find((x) => x.text);
                                    if (p) phoneticText = p.text;
                                }
                            }
                        }
                    } catch (e1) {}

                    if (!phoneticText) {
                        const res2 = await fetch(`https://rhymebrain.com/talk?function=getWordInfo&word=${word}`);
                        if (res2.ok) {
                            const json2 = await res2.json();
                            if (json2 && json2.ipa) {
                                phoneticText = `/${json2.ipa}/`;
                            }
                        }
                    }

                    if (phoneticText) {
                        setPhonetics((prev) => ({ ...prev, [word]: phoneticText }));
                    }
                } catch (e) {}
            }
        };
        fetchPhonetics();
    }, [allCleanWords]);

    // Initialize speech recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setRecognitionError("Tu navegador no soporta el reconocimiento de voz. Te recomendamos usar Google Chrome.");
            return;
        }

        const recognition = new SpeechRecognition();
        const langCode = moduloactual.cursos_item.html || "en-US";
        recognition.lang = langCode;
        recognition.interimResults = true;
        recognition.continuous = true;

        recognition.onresult = (event) => {
            let finalTranscript = "";
            let interimTranscript = "";
            for (let i = 0; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript + " ";
                } else {
                    interimTranscript += event.results[i][0].transcript + " ";
                }
            }
            const fullText = (finalTranscript + interimTranscript).trim();
            if (activeListeningIndex !== null) {
                setCardTranscripts((prev) => ({ ...prev, [activeListeningIndex]: fullText }));
            }
        };

        recognition.onerror = (event) => {
            console.error("Speech recognition error", event.error);
            setActiveListeningIndex(null);
        };

        recognition.onend = () => {
            setActiveListeningIndex(null);
        };

        recognitionRef.current = recognition;

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, [activeListeningIndex]);

    // Calculate score when transcript updates for active card
    useEffect(() => {
        if (activeListeningIndex === null) return;
        const currentLine = structuredLines[activeListeningIndex];
        if (!currentLine) return;

        const transcript = cardTranscripts[activeListeningIndex] || "";
        if (!transcript) return;

        const spokenWords = transcript.split(/\s+/).filter((w) => w.length > 0).map(cleanWord);
        const lineWordsClean = currentLine.words.map((w) => w.clean);

        let matches = 0;
        let lastFoundIndex = -1;

        lineWordsClean.forEach((targetWord) => {
            const foundIndex = spokenWords.findIndex((sw, idx) => idx > lastFoundIndex && sw === targetWord);
            if (foundIndex !== -1) {
                matches++;
                lastFoundIndex = foundIndex;
            }
        });

        const lineScore = Math.min(100, Math.round((matches / lineWordsClean.length) * 100));
        setCardScores((prev) => ({ ...prev, [activeListeningIndex]: lineScore }));

        if (lineScore >= 70) {
            setCardPassed((prev) => {
                const next = { ...prev, [activeListeningIndex]: true };
                const totalLines = structuredLines.length;
                const passedCount = Object.keys(next).filter((k) => next[k]).length;
                if (passedCount >= totalLines && !moduloactual.hecho) {
                    markedAsDone && markedAsDone(moduloactual);
                }
                return next;
            });
        }
    }, [cardTranscripts, activeListeningIndex]);

    const toggleListeningCard = (index) => {
        if (activeListeningIndex === index) {
            recognitionRef.current?.stop();
            setActiveListeningIndex(null);
        } else {
            if (recognitionRef.current && activeListeningIndex !== null) {
                recognitionRef.current.stop();
            }
            setCardTranscripts((prev) => ({ ...prev, [index]: "" }));
            setCardScores((prev) => ({ ...prev, [index]: 0 }));
            setActiveListeningIndex(index);
            try {
                recognitionRef.current?.start();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const resetCard = (index) => {
        if (activeListeningIndex === index) {
            recognitionRef.current?.stop();
            setActiveListeningIndex(null);
        }
        setCardTranscripts((prev) => ({ ...prev, [index]: "" }));
        setCardScores((prev) => ({ ...prev, [index]: 0 }));
        setCardPassed((prev) => ({ ...prev, [index]: false }));
    };

    const playLineAudio = (lineText, index) => {
        if (!("speechSynthesis" in window)) {
            alert("Tu navegador no soporta lectura de texto.");
            return;
        }
        setPlayingIndex(index);
        const utterance = new SpeechSynthesisUtterance(lineText);
        utterance.lang = moduloactual.cursos_item.html || "en-US";
        utterance.rate = 0.8;

        utterance.onend = () => setPlayingIndex(null);
        utterance.onerror = () => setPlayingIndex(null);

        let voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
            const lang = (moduloactual.cursos_item.html || "en-US").toLowerCase();
            const primaryLang = lang.split("-")[0];
            let selectedVoice = voices.find((v) => v.lang.toLowerCase() === lang || v.lang.toLowerCase().startsWith(primaryLang));
            if (selectedVoice) utterance.voice = selectedVoice;
        }
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
    };

    const allPassed = structuredLines.length > 0 && structuredLines.every((_, idx) => cardPassed[idx] || moduloactual.hecho);

    return (
        <div className="pronunciation-practice-container my-3">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h4 className="mb-0 font-weight-bold text-dark">Práctica de Pronunciación por Bloques</h4>
                {allPassed && (
                    <span className="badge badge-success px-3 py-2 fs-6">
                        <CheckmarkCircle color="#fff" height="18px" width="18px" className="mr-1" /> Actividad Completada
                    </span>
                )}
            </div>

            {recognitionError && <div className="alert alert-danger mb-4">{recognitionError}</div>}

            {structuredLines.map((lineObj) => {
                const index = lineObj.index;
                const transcript = cardTranscripts[index] || "";
                const score = cardScores[index] || (moduloactual.hecho ? 100 : 0);
                const isPassed = cardPassed[index] || moduloactual.hecho;
                const isListening = activeListeningIndex === index;
                const isPlaying = playingIndex === index;

                const spokenWords = transcript.split(/\s+/).filter((w) => w.length > 0).map(cleanWord);
                let lastFoundIndex = -1;

                return (
                    <div key={index} className={`card shadow-sm mb-4 border-0 rounded-lg ${isListening ? "border-primary shadow" : ""}`} style={{ transition: "all 0.3s ease" }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                <span className="badge badge-secondary text-uppercase px-2 py-1">Bloque {index + 1}</span>
                                {isPassed ? (
                                    <span className="text-success font-weight-bold d-flex align-items-center">
                                        <CheckmarkCircle color="#28a745" height="20px" width="20px" className="mr-1" /> Correcto (100%)
                                    </span>
                                ) : (
                                    <span className="text-muted small">Precisión: <strong className={score >= 70 ? "text-success" : "text-primary"}>{score}%</strong></span>
                                )}
                            </div>

                            {/* Text & Phonetics */}
                            <div className="my-3 text-center py-3 bg-light rounded" style={{ minHeight: "100px" }}>
                                <div className="d-flex flex-wrap align-items-center justify-content-center">
                                    {lineObj.words.map((wObj, wIdx) => {
                                        const clean = wObj.clean;
                                        let isMatched = false;
                                        if (transcript.length > 0) {
                                            const foundIndex = spokenWords.findIndex((sw, idx) => idx > lastFoundIndex && sw === clean);
                                            if (foundIndex !== -1) {
                                                isMatched = true;
                                                lastFoundIndex = foundIndex;
                                            }
                                        }

                                        let colorStatus = "text-dark";
                                        if (transcript.length > 0) {
                                            colorStatus = isMatched ? "text-success font-weight-bold" : "text-danger";
                                        }

                                        return (
                                            <div key={wIdx} className="d-inline-flex flex-column align-items-center mx-2 my-1">
                                                <span className={`${colorStatus}`} style={{ fontSize: "1.8rem", transition: "color 0.2s" }}>
                                                    {wObj.word}
                                                </span>
                                                {phonetics[clean] && (
                                                    <small className="text-secondary" style={{ fontSize: "0.95rem", opacity: 0.85 }}>
                                                        {phonetics[clean]}
                                                    </small>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Progress bar per card */}
                            <div className="progress mb-3" style={{ height: "6px" }}>
                                <div
                                    className={`progress-bar ${isPassed || score >= 70 ? "bg-success" : "bg-primary"}`}
                                    role="progressbar"
                                    style={{ width: `${isPassed ? 100 : score}%`, transition: "width 0.4s" }}
                                ></div>
                            </div>

                            {/* Action buttons for THIS block */}
                            <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                                <button
                                    className={`btn ${isPlaying ? "btn-info text-white" : "btn-outline-info"} rounded-pill px-3 py-2 d-inline-flex align-items-center mr-2`}
                                    onClick={() => playLineAudio(lineObj.rawLine, index)}
                                >
                                    <VolumeHighOutline color={isPlaying ? "#fff" : "#17a2b8"} height="22px" width="22px" className="mr-1" />
                                    <span>Escuchar</span>
                                </button>

                                <button
                                    className={`btn ${isListening ? "btn-danger" : "btn-primary"} rounded-pill px-4 py-2 d-inline-flex align-items-center mr-2`}
                                    onClick={() => toggleListeningCard(index)}
                                >
                                    {isListening ? (
                                        <>
                                            <Stop color="#fff" height="22px" width="22px" className="mr-1" />
                                            <span>Detener</span>
                                        </>
                                    ) : (
                                        <>
                                            <MicOutline color="#fff" height="22px" width="22px" className="mr-1" />
                                            <span>Practicar</span>
                                        </>
                                    )}
                                </button>

                                <button
                                    className="btn btn-outline-secondary rounded-circle p-2 d-inline-flex align-items-center justify-content-center"
                                    onClick={() => resetCard(index)}
                                    title="Reiniciar bloque"
                                    style={{ width: "40px", height: "40px" }}
                                >
                                    <RefreshOutline color="#6c757d" height="20px" width="20px" />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PronunciationPractice;
